import { Table2 } from "components"
import { useMemo } from "react"
import { addLeadingZeros, convertIOSDateToHTMLFormatTime, isInTimeWindow, ordinalSuffix } from "config/utils"
import tickMark from "images/tickMark.png"
import crossMark from "images/crossMark.png"
import clockMark from "images/clockMark.png"

import { PreviousShiftEvent } from "@type/workMonitor.types"
import moment from "moment"

interface Props {
	checkEvents: PreviousShiftEvent[]
	checkCallWindow: string
}

const CheckCallsTable: React.FC<Props> = ({ checkEvents, checkCallWindow }) => {
	if (!checkEvents.length) return null
	const { rows, columns, totalCheckCalls, failedCheckCalls, onTimeCheckCalls, lateCheckCalls } = useMemo(() => {
		const lateCheckAllowed = parseInt(checkCallWindow, 10)
		const totalCheckCalls = checkEvents.length
		const failedCheckCalls = checkEvents.filter(({ isEventMissed }) => isEventMissed).length
		const lateCheckCalls = checkEvents.filter(({ dueTime, eventTime }) =>
			isInTimeWindow(moment(dueTime), moment(eventTime), { after: lateCheckAllowed }),
		).length

		const onTimeCheckCalls = totalCheckCalls - lateCheckCalls - failedCheckCalls

		const rows = checkEvents.map(({ dueTime, eventTime }, i) => {
			const dueTimeFormatted = dueTime ? convertIOSDateToHTMLFormatTime(dueTime) : "-"
			const submitTimeFormatted = eventTime ? convertIOSDateToHTMLFormatTime(eventTime) : "-"
			const isLate = eventTime
				? isInTimeWindow(moment(dueTime), moment(eventTime), { after: lateCheckAllowed })
				: false

			return {
				index: i + 1,
				dueTimeFormatted,
				submitTimeFormatted,
				eventTime,
				isLate,
			}
		})

		const columns = [
			{
				label: "Check Call",
				key: "index",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-1/4",
				render: ({ row }: any) => (
					<div>
						{row.index}
						{ordinalSuffix(row.index)} CHECK CALL
					</div>
				),
			},
			{
				label: "Due Time",
				key: "dueTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-1/4",
				render: ({ row }: any) => (
					<div className="text-gray-500">
						DUE TIME:
						<span className="ml-2.5 text-gray-900">{row.dueTimeFormatted}</span>
					</div>
				),
			},
			{
				label: "Submit Time",
				key: "submitTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-1/4",
				render: ({ row }: any) => (
					<div className={`flex ${!row.eventTime ? "text-[#B42318]" : "text-gray-500"}`}>
						SUBMIT TIME:
						<span className="ml-2.5 text-gray-900">{row.submitTimeFormatted}</span>
						{row.isLate && (
							<span className="ml-1.5 flex items-center gap-1 rounded-4 bg-yellow-100 px-2 text-orange-700">
								<img className="h-3 w-3" src={clockMark} alt="Late icon" />
								Late
							</span>
						)}
					</div>
				),
			},
			{
				label: "Status",
				key: "status",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-1/4",
				render: ({ row }: any) => (
					<div className="flex space-x-2.5 text-gray-500">
						<span>STATUS:</span>
						<div
							className={`flex items-center gap-1 rounded-4 px-2 font-medium ${
								row.eventTime
									? "w-12.5 bg-[#ECFDF3] text-[#027A48]"
									: "w-12.5 bg-[#FEF3F2] text-[#B42318]"
							}`}
						>
							<img
								className="h-3 w-3"
								src={row.eventTime ? tickMark : crossMark}
								alt={row.eventTime ? "tick" : "cross"}
							/>
							{row.eventTime ? "Passed" : "Failed"}
						</div>
					</div>
				),
			},
		]

		return { rows, columns, totalCheckCalls, failedCheckCalls, onTimeCheckCalls, lateCheckCalls }
	}, [checkEvents, checkCallWindow])
	return (
		<div className="space-y-5">
			<div className="flex w-full justify-between bg-[#B2DDFF] py-3 px-6 text-sm">
				<div className="font-medium">CHECK CALLS</div>
				<div className="flex space-x-10">
					<div className="flex space-x-3">
						<span className="font-medium">TOTAL:</span>
						<span>{addLeadingZeros(totalCheckCalls, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">ON TIME:</span>
						<span>{addLeadingZeros(onTimeCheckCalls, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">LATE SUBMITTED:</span>
						<span>{addLeadingZeros(lateCheckCalls, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">FAILED:</span>
						<span>{addLeadingZeros(failedCheckCalls, 2)}</span>
					</div>
				</div>
			</div>
			<Table2
				tableClassName="w-full text-sm"
				hideHeader={true}
				columns={columns}
				rows={rows}
				rowClassName="h-0 border border-0.125 border-[#667085]"
			/>
		</div>
	)
}

export default CheckCallsTable
